/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { SalesOffice } from "@eisinc/App/Components/SalesOffice/SalesOffice";
import { companyLogoItem } from "@eisinc/App/SharedStyles/GeneralStyles";
import { screen } from "@eisinc/XDL/Helpers";
import printStyles, { onPrint } from "@eisinc/XDL/Utilities/print";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import { PageErrorWrapper } from "@insite/client-framework/Components/PageErrorWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadOrderStatusMappings from "@insite/client-framework/Store/Data/OrderStatusMappings/Handlers/LoadOrderStatusMappings";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import displayOrder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/DisplayOrder";
import resetOrderNumber from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/ResetOrderNumber";
import setOrderNumber from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/SetOrderNumber";
import setVmiOrderDetailsPage from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/SetVmiOrderDetailsPage";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem } from "@insite/mobius/index";
import Page from "@insite/mobius/Page";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);

    let orderNumber;
    let isVmiOrder = false;
    if (location && location.search) {
        const parsedQuery = parseQueryStringCaseInsensitive(location.search, {
            orderNumber: "",
            isVmiOrder: false,
        });
        orderNumber = parsedQuery.orderNumber;
        isVmiOrder = parsedQuery.isVmiOrder;
    }

    const orderState = getOrderState(state, orderNumber);
    const orderStatusMappingDataView = getOrderStatusMappingDataView(state);
    const isOrderNumberStoredInState = state.pages.orderDetails.orderNumber === orderNumber;

    return {
        location,
        orderNumber,
        orderState,
        shouldDisplayOrder: !isOrderNumberStoredInState,
        shouldLoadOrderStatusMappings: !orderStatusMappingDataView.value && !orderStatusMappingDataView.isLoading,
        isVmiOrder,
        isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,
        isOrderNumberStoredInState,
    };
};

const mapDispatchToProps = {
    setOrderNumber,
    displayOrder,
    loadOrderStatusMappings,
    setVmiOrderDetailsPage,
    resetOrderNumber,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & PageProps & ReturnType<typeof mapStateToProps>;

export interface OrderDetailsPageStyles {
    titleStatusWrapper?: InjectableCss;
    loadFailedWrapper?: InjectableCss;
    loadFailedText?: TypographyPresentationProps;
    headerGridItem?: GridItemProps;
    titleItemWrapper?: InjectableCss;
    titleItem?: InjectableCss;
    orderStatusItem?: InjectableCss;
    buttonSetGridItem?: InjectableCss;
    messageRichContentGridItem?: GridItemProps;
    repInfoOrderedByGridItem?: GridItemProps;
    repInfoGridItem?: GridItemProps;
    orderedByGridItem?: GridItemProps;
    salesOfficeGridItem?: GridItemProps;
    dateStatusPOGridItem?: GridItemProps;
    billingInfoGridItem?: GridItemProps;
    shippingInfoGridItem?: GridItemProps;
    shipmentPackagesWrapper?: InjectableCss;
    orderSummaryGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    orderDetailsInformationGridItem?: GridItemProps;
    printableSalesInfoWrapper?: GridContainerProps;
    orderLines?: GridItemProps;
    orderInformationWrapper?: GridItemProps;
}

export const orderDetailsPageStyles: OrderDetailsPageStyles = {
    loadFailedWrapper: {
        css: css`
            display: flex;
            height: 200px;
            justify-content: center;
            align-items: center;
            background-color: ${getColor("common.accent")};
        `,
    },
    loadFailedText: {
        weight: "bold",
    },
    titleStatusWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;

            ${screen("lte", "md")`flex-direction: column;`}
        `,
    },
    headerGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            line-height: 1;
        `,
    },
    titleItemWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            line-height: 38px;

            ${onPrint(css`
                flex-direction: column;
                align-items: start;
                line-height: 25px;
            `)}
        `,
    },
    titleItem: {
        css: css`
            width: auto;
            margin-right: 30px;
        `,
    },
    orderStatusItem: {
        css: css`
            width: auto;
        `,
    },
    buttonSetGridItem: {
        css: css`
            display: flex;
            ${screen("lte", "sm")`
                width: 100%;
                margin-top: 8px;
            `}
        `,
    },
    messageRichContentGridItem: {
        width: 12,
        css: css`
            padding: 0;
            @media print and (orientation: landscape) {
                padding-top: 10px;
            }
        `,
    },
    orderInformationWrapper: {
        width: 12,
        css: css`
            &&& {
                margin-inline: 16px;
            }
            padding-inline: 0;
            border-top: 1px solid ${getColor("common.ui2")};
        `,
    },
    repInfoGridItem: {
        width: 12,
    },
    orderedByGridItem: {
        width: 12,
    },
    salesOfficeGridItem: {
        width: 12,
        css: css`
            display: none;
            ${printStyles.showOnPrint}
        `,
    },
    dateStatusPOGridItem: {
        width: [12, 6, 6, 3, 3],
        printWidth: 6,
        css: css`
            padding-bottom: 0;
        `,
    },
    billingInfoGridItem: {
        width: [12, 6, 6, 3, 3],
        printWidth: 6,
        css: css`
            padding-bottom: 0;
        `,
    },
    shippingInfoGridItem: {
        width: [12, 6, 6, 3, 3],
        printWidth: 6,
        css: css`
            padding-bottom: 0;
        `,
    },
    repInfoOrderedByGridItem: {
        width: [12, 6, 6, 3, 3],
        printWidth: 6,
    },
    orderSummaryGridItem: {
        width: [12, 12, 4, 3, 3],
        printWidth: 4,
        css: css`
            padding-top: 0;
            ${screen("lte", "md")`padding-top: 24px;`}
            margin-left: auto;
            ${screen("gte", "md")`padding-right: 0;`}
        `,
    },
    shipmentPackagesWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 30px;
            ${onPrint(css`
                margin-top: 30px;
                overflow-x: hidden;
                td {
                    white-space: normal;
                }
            `)}
        `,
    },
    notesGridItem: {
        width: 12,
    },
    orderDetailsInformationGridItem: {
        width: [12, 12, 8, 9, 9],
        printWidth: 8,
    },
    printableSalesInfoWrapper: {
        css: css`
            display: none;
            ${printStyles.showOnPrint}
        `,
    },
    orderLines: {
        width: 12,
        css: css`
            @media screen {
                margin-top: 30px;
            }
        `,
    },
};

export const shipmentPackages: any = {};
export const orderLines: any = {};
export const orderLineShipmentData: any = {};

export let shipmentsTrackingData: any = {};

export const getOrderLineShipmentData = (id: string): any => {
    if (orderLineShipmentData[id]) {
        return orderLineShipmentData[id];
    }

    orderLineShipmentData[id] = [];

    for (const key in shipmentPackages) {
        // eslint-disable-next-line no-prototype-builtins
        if (!shipmentPackages.hasOwnProperty(key)) {
            continue;
        }

        const shipmentPackage = shipmentPackages[key];
        const shipmentPackageData = shipmentsTrackingData[shipmentPackage.id];
        const shipmentPackageProductLines = shipmentPackage?.shipmentPackageLineDtos;

        if (shipmentPackageProductLines && shipmentPackageProductLines.length > 0) {
            for (let i = 0; i < shipmentPackageProductLines.length; i++) {
                const shipmentPackageProductLine = shipmentPackageProductLines[i];

                if (shipmentPackageProductLine.orderLineId === id) {
                    const obj = {} as any;

                    obj.id = shipmentPackageProductLine.id;
                    obj.shipmentDate = shipmentPackage.shipmentDate;
                    obj.trackingNumber = shipmentPackageData?.[0]?.Number;
                    obj.trackingUrl = shipmentPackageData?.[0]?.Url;
                    obj.deliveryNumber = shipmentPackageData?.DeliveryNumber;
                    obj.qtyShipped = shipmentPackageProductLine.qtyShipped;

                    orderLineShipmentData[id].push(obj);
                }
            }
        }
    }

    return orderLineShipmentData[id];
};

class OrderDetailsPage extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        // The share entity (for orders) functionality can use this page
        // to render HTML for PDF generation, so we need to leave this SSR
        // ability so that functionality works.
        if (this.props.shouldLoadOrderStatusMappings) {
            this.props.loadOrderStatusMappings();
        }
        if (this.props.orderNumber && this.props.shouldDisplayOrder) {
            this.props.displayOrder({
                orderNumber: this.props.orderNumber,
                expand: this.props.isVmiOrder ? ["orderLines", "shipments", "vmidetails"] : ["orderLines", "shipments"],
            });
        }
        if (this.props.isVmiOrderDetailsPage) {
            this.props.setVmiOrderDetailsPage(false);
        }
    }

    componentDidUpdate(): void {
        if (this.props.orderNumber && !this.props.isOrderNumberStoredInState) {
            this.props.setOrderNumber({ orderNumber: this.props.orderNumber });
        }
    }

    componentWillUnmount() {
        this.props.resetOrderNumber();
    }

    render() {
        const { orderState, orderNumber } = this.props;
        const styles = orderDetailsPageStyles;

        // During share entity SSR (PDF generation), ensure we don't render content
        // until order data is fully loaded. This prevents blank PDFs caused by
        // rendering before data arrives. The SSR loop will re-render once data loads.
        if (getIsShareEntity() && orderNumber && (!orderState.value || orderState.isLoading)) {
            return (
                <Page>
                    <div>Loading order {orderNumber}...</div>
                </Page>
            );
        }

        const order = this.props.orderState && this.props.orderState.value;

        if (order && order.orderLines && order.orderLines.length > 0) {
            for (let i = 0; i < order.orderLines.length; i++) {
                const orderLine = order.orderLines[i];

                orderLines[orderLine.id] = orderLine;
            }
        }

        if (order && order.shipmentPackages && order.shipmentPackages.length > 0) {
            for (let i = 0; i < order.shipmentPackages.length; i++) {
                const shipmentPackage = order.shipmentPackages[i];

                shipmentPackages[shipmentPackage.id] = shipmentPackage;
            }
        }

        if (order && order.properties && order.properties.shipmentsTrackingData) {
            shipmentsTrackingData = JSON.parse(order.properties.shipmentsTrackingData);
        }

        return (
            <Page>
                <PageErrorWrapper
                    value={this.props.orderState}
                    badRequestMessage={siteMessage("Order_BadRequest")}
                    notAuthorizedMessage={siteMessage("Order_NotAuthorized")}
                    notFoundMessage={siteMessage("Order_NotFound")}
                    extendedStyles={orderDetailsPageStyles}
                    loadFailedTextTestSelector="orderDetails_pageNotFound"
                >
                    <OrderStateContext.Provider value={this.props.orderState}>
                        <GridContainer>
                            <GridItem {...styles.headerGridItem}>
                                <StyledWrapper {...styles.titleItemWrapper}>
                                    <StyledWrapper {...companyLogoItem}>
                                        <img src={siteMessage("Company_Logo") as any} alt={translate("Company Logo")} />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.titleItem}>
                                        <Zone contentId={this.props.id} zoneName="TitleWidget" />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.orderStatusItem}>
                                        <Zone contentId={this.props.id} zoneName="OrderStatus" />
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper {...styles.buttonSetGridItem}>
                                    <Zone contentId={this.props.id} zoneName="ButtonSetWidget" />
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.orderInformationWrapper}>
                                <GridContainer>
                                    <GridItem {...styles.orderDetailsInformationGridItem}>
                                        <GridContainer>
                                            <GridItem {...styles.dateStatusPOGridItem}>
                                                <Zone contentId={this.props.id} zoneName="DateStatusPOWidgets" />
                                            </GridItem>
                                            <GridItem {...styles.billingInfoGridItem}>
                                                <Zone contentId={this.props.id} zoneName="BillingInfoWidget" />
                                            </GridItem>
                                            <GridItem {...styles.shippingInfoGridItem}>
                                                <Zone contentId={this.props.id} zoneName="ShippingInfoWidget" />
                                            </GridItem>
                                            <GridItem {...styles.repInfoOrderedByGridItem}>
                                                <GridContainer gap={2}>
                                                    <GridItem {...styles.repInfoGridItem}>
                                                        <Zone contentId={this.props.id} zoneName="RepInfoWidget" />
                                                    </GridItem>
                                                    <GridItem {...styles.orderedByGridItem}>
                                                        <Zone contentId={this.props.id} zoneName="OrderedByWidget" />
                                                    </GridItem>
                                                    {order && order.properties && order.properties.salesOffice && (
                                                        <GridItem {...styles.salesOfficeGridItem}>
                                                            <SalesOffice
                                                                salesOfficeData={order.properties.salesOffice}
                                                                clickable={true}
                                                            />
                                                        </GridItem>
                                                    )}
                                                </GridContainer>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                    <GridItem {...styles.orderSummaryGridItem}>
                                        <Zone contentId={this.props.id} zoneName="OrderSummaryWidget" />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.orderLines}>
                                <Zone contentId={this.props.id} zoneName="MessageRichContentWidget" />
                            </GridItem>
                            <GridItem {...styles.orderLines}>
                                <Zone contentId={this.props.id} zoneName="NotesWidget" />
                            </GridItem>
                            <GridItem {...styles.orderLines}>
                                <StyledWrapper {...styles.shipmentPackagesWrapper}>
                                    <Zone contentId={this.props.id} zoneName="ShipmentPackagesWidget" />
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.orderLines}>
                                <Zone contentId={this.props.id} zoneName="OrderLinesWidget" />
                            </GridItem>
                        </GridContainer>
                    </OrderStateContext.Provider>
                </PageErrorWrapper>
                <Modals />
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderDetailsPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "OrderDetailsPage" instead of this constant.
 */
export const OrderDetailsPageContext = "OrderDetailsPage";
